import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Trophy, 
  PlusCircle, 
  ArrowLeft, 
  ChevronRight,
  X,
  Coins,
  Wallet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import MatchPlayEventDetail from "../components/MatchPlayEventDetail";
import EntryFeeSelector from "../components/EntryFeeSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create Match Play Modal
const CreateMatchPlayModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [totalWeeks, setTotalWeeks] = useState(18);
  const [maxPlayers, setMaxPlayers] = useState(50);
  const [entryFee, setEntryFee] = useState(0);
  const [entryFeeType, setEntryFeeType] = useState('coins');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Please enter a league name");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/match-play/events`, {
        name: name.trim(),
        description: description.trim(),
        total_weeks: totalWeeks,
        max_players: maxPlayers,
        entry_fee: entryFee,
        entry_fee_type: entryFee > 0 ? entryFeeType : null
      });
      toast.success("League created successfully!");
      
      // Dispatch event to refresh Golf Coins balance in header (coins deducted on create)
      if (entryFee > 0 && entryFeeType === 'coins') {
        window.dispatchEvent(new Event('golfCoinsUpdated'));
      }
      
      onCreated(response.data.event_id);
    } catch (error) {
      toast.error("Failed to create league");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 pt-16 md:pt-20 overflow-y-auto">
      <Card className="w-full max-w-lg animate-fade-in my-4 mb-20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Create Match Play League</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1 block">League Name *</label>
            <Input 
              placeholder="e.g., Sunday Match Play League" 
              value={name} 
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1 block">Description (optional)</label>
            <textarea 
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
              rows={3}
              placeholder="Weekly 9-hole match play at Pebble Beach..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Season Length (weeks)</label>
              <select
                value={totalWeeks}
                onChange={(e) => setTotalWeeks(parseInt(e.target.value))}
                className="w-full p-2 border border-stone-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                {Array.from({ length: 36 }, (_, i) => i + 1).map((week) => (
                  <option key={week} value={week}>{week} week{week !== 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Max Players</label>
              <select
                value={maxPlayers}
                onChange={(e) => setMaxPlayers(parseInt(e.target.value))}
                className="w-full p-2 border border-stone-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                {[4, 6, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 44, 48, 50].map((num) => (
                  <option key={num} value={num}>{num} players</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4 text-sm">
            <h4 className="font-medium text-green-900 mb-2">Match Play Format</h4>
            <ul className="text-green-700 space-y-1">
              <li>• Two-player teams compete head-to-head</li>
              <li>• 5 points for win, 2.5 points for tie</li>
              <li>• Player handicaps calculated after 3 rounds</li>
              <li>• Weekly 9-hole rounds with flexible entry</li>
            </ul>
          </div>
          
          {/* Entry Fee Section */}
          <div className="border-t pt-4">
            <label className="text-sm font-medium text-stone-700 mb-3 block flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              Entry Fee (Optional)
            </label>
            <EntryFeeSelector
              entryFee={entryFee}
              entryFeeType={entryFeeType}
              onChangeValue={setEntryFee}
              onChangeType={setEntryFeeType}
              playerCount={maxPlayers}
              showTotalPot={true}
              compact={true}
            />
          </div>
        </CardContent>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleCreate} disabled={loading} className="flex-1 btn-primary">
            {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : 'Create League'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

const MatchPlayPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const selectedEvent = searchParams.get('event');
  const setSelectedEvent = eventId => setSearchParams(eventId ? { event: eventId } : {});
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/match-play/events`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedEvent) fetchEvents();
  }, [selectedEvent]);

  if (selectedEvent) {
    return <MatchPlayEventDetail key={selectedEvent} eventId={selectedEvent} onBack={() => setSelectedEvent(null)} user={user} />;
  }

  return (
    <div className="ps-theme-page max-w-4xl mx-auto animate-fade-in pt-2" data-testid="match-play-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/tournaments')} className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-stone-900 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-green-600" />
            Match Play Leagues
          </h1>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-green-600 hover:bg-green-700 text-white">
          <PlusCircle className="w-4 h-4 mr-2" />
          Create League
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center">
            <div className="spinner w-8 h-8 border-green-600 border-t-transparent" />
          </div>
        </Card>
      ) : events.length === 0 ? (
        <Card className="p-8 text-center">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-stone-300" />
          <h2 className="text-xl font-bold text-stone-900 mb-2">No Leagues Yet</h2>
          <p className="text-stone-500 mb-6 max-w-md mx-auto">
            Create your first Match Play league to start head-to-head team competitions with automatic handicap tracking.
          </p>
          <Button onClick={() => setShowCreateModal(true)} className="bg-green-600 hover:bg-green-700 text-white">
            <PlusCircle className="w-4 h-4 mr-2" />
            Create Your First League
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <Card 
              key={event.event_id} 
              className="cursor-pointer hover:border-green-300 hover:shadow-md transition-all"
              onClick={() => setSelectedEvent(event.event_id)}
              data-testid={`match-open-${event.event_id}`} role="button" tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedEvent(event.event_id); } }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      event.status === 'active' ? 'bg-green-100' : 'bg-stone-100'
                    }`}>
                      <Trophy className={`w-6 h-6 ${event.status === 'active' ? 'text-green-600' : 'text-stone-500'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900">{event.name}</h3>
                      <p className="text-sm text-stone-500">
                        {event.team_count} teams • {event.total_weeks} weeks • {event.creator_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      event.status === 'active' ? 'bg-green-100 text-green-700' :
                      event.status === 'completed' ? 'bg-stone-100 text-stone-600' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {event.status === 'active' ? 'Active' : event.status === 'completed' ? 'Completed' : 'Setup'}
                    </span>
                    <ChevronRight className="w-5 h-5 text-stone-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateMatchPlayModal 
          onClose={() => setShowCreateModal(false)} 
          onCreated={(eventId) => {
            setShowCreateModal(false);
            setSelectedEvent(eventId);
            fetchEvents();
          }}
        />
      )}
    </div>
  );
};

export default MatchPlayPage;
